import axiosInstance from './axiosCreator';
import { SchedulingRequestBody } from '../types/Study';
import { enqueueSnackbar } from 'notistack';
import { todaySessionReceived } from '../slices/todaySession/todaySessionSlice';
import { store } from '../store/store';
import { setVignettes } from '../slices/vignettes/vignettesSlice';
import { blocksLoaded } from '../slices/blocks/blocksSlice';
import { flashcardsLoaded } from '../slices/flashcards/flashcardsSlice';
import {
  selectShouldFetchTopics,
  topicsReceived,
  topicsLoading,
  topicsFailed,
} from '../slices/todayTopics/todayTopicsSlice';
import { Flashcard } from '../types/Flashcard';

interface ReportContentRequest {
  email: string;
  contentId: string;
  contentText: string;
  reason: string;
}

export const fetchTodayStudySession = async (reset?: boolean) => {
  try {
    const response = await axiosInstance.get('/study/session', {
      params: {
        reset,
      },
    });
    store.dispatch(todaySessionReceived(response.data.data));
    return response.data.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const fetchStudySessionByIdForReview = async (
  sessionId: string,
  blockId: string
) => {
  try {
    const response = await axiosInstance.get(`/study/session/${sessionId}`);
    const studySession = {
      ...response.data.data,
      isReview: true,
      activeBlockId: blockId,
    };
    store.dispatch(todaySessionReceived(studySession));
    return studySession;
  } catch (error) {
    console.error(error);
  }
};

export const fetchTodayBlocks = async () => {
  try {
    const response = await axiosInstance.get('/study/blocks');
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const fetchBlocksByStudySessionId = async (sessionId: string) => {
  try {
    const response = await axiosInstance.get(
      `/study/session/${sessionId}/blocks`
    );
    store.dispatch(blocksLoaded(response.data.blocks));
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const fetchVignettesForBlock = async (blockId: string) => {
  try {
    const response = await axiosInstance.get(
      `/study/blocks/${blockId}/vignettes`
    );

    if (response.data.vignettes?.length > 0) {
      store.dispatch(setVignettes(response.data.vignettes));
    }

    return response.data;
  } catch (error) {
    console.error(error);
    return null; // Return null or appropriate error state
  }
};

interface FlashcardsForStudySessionResponse {
  new: Flashcard[];
  due: Flashcard[];
  completed: Flashcard[];
}

export const fetchFlashcardsForStudySession = async (
  sessionId: string
): Promise<FlashcardsForStudySessionResponse> => {
  try {
    const response = await axiosInstance.get(
      `/study/session/${sessionId}/flashcards`
    );
    // if (response.data.new) {
    //   cacheFlashcardImages(response.data.new);
    // }
    // if (response.data.due) {
    //   cacheFlashcardImages(response.data.due);
    // }
    store.dispatch(
      flashcardsLoaded({
        new: response.data.new,
        due: response.data.due,
        completed: response.data.completed,
      })
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return { new: [], due: [], completed: [] };
  }
};

export const updateFlashcardSchedulingBE = async (
  flashcardId: string,
  schedulingObj: SchedulingRequestBody
) => {
  try {
    const response = await axiosInstance.put(
      `/study/flashcards/${flashcardId}/scheduling`,
      schedulingObj
    );
    return response.data;
  } catch (error) {
    enqueueSnackbar(
      'Failed to update flashcard. Please check your network connection or refresh.',
      {
        autoHideDuration: 5000,
        variant: 'error',
      }
    );
    console.error(error);
  }
};

export const reportContent = async (reportInfo: ReportContentRequest) => {
  try {
    const response = await axiosInstance.post(
      '/study/content/report',
      reportInfo
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const resetStudyProgress = async () => {
  try {
    const response = await axiosInstance.post('/study/reset');
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const fetchStudiedContentCounts = async () => {
  try {
    const response = await axiosInstance.get('/study/studiedCounts');
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const modifyStudySession = async (
  studySessionId: string,
  flashcardCount: number,
  vignetteCount: number
) => {
  try {
    const response = await axiosInstance.post(`/study/modifyStudySession`, {
      studySessionId,
      flashcardCount,
      vignetteCount,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const fetchTodayTopicsIfNeeded = async (date: string) => {
  const state = store.getState();
  const shouldFetch = selectShouldFetchTopics(date)(state);

  if (!shouldFetch) {
    return;
  }

  store.dispatch(topicsLoading());

  try {
    const response = await fetchStudyTopicsForDate(date);
    if (response?.topics) {
      store.dispatch(topicsReceived({ topics: response.topics, date }));
    } else {
      store.dispatch(topicsReceived({ topics: [], date }));
    }
  } catch (error) {
    console.error('Error fetching topics for date:', date, error);
    store.dispatch(
      topicsFailed(error instanceof Error ? error.message : 'Unknown error')
    );
  }
};

export const fetchStudyTopicsForDate = async (date: string) => {
  try {
    const response = await axiosInstance.get(`/study/topics?date=${date}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const fetchStudyLecturesForDate = async (date: string) => {
  try {
    const response = await axiosInstance.get(`/study/lectures?date=${date}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
